import { IToolPanelParams } from "ag-grid-community";
import { Button, ToggleButton, ToggleButtonGroup, Divider, useTheme } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import type { Dayjs } from "dayjs";
import type { BlotterFeedType } from "../../../services/blotterApi";
import {
  ORDER_EVENT,
  POSITION_UPDATE,
  BALANCE_UPDATE,
  ASSET_MARGIN_REPORT,
  POSITION_MARGIN_REPORT,
  TRADE_EVENT,
  TRANSFER_EVENT,
} from "@/services/consts";
import "./BlotterControlsPanel.styles.scss";

export interface BlotterControlsPanelProps extends IToolPanelParams {
  feedType: BlotterFeedType;
  onFeedTypeChange: (type: BlotterFeedType) => void;
  startDate: Dayjs | null;
  onStartDateChange: (date: Dayjs | null) => void;
  boundDates: { min: Dayjs | null; max: Dayjs | null };
  historicalTimestamp: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  onExpandAll: () => void;
  allExpanded: boolean;
  showExpandAll: boolean;
  hasAccount: boolean;
}

/**
 * BlotterControlsPanel - Custom AG Grid sidebar tool panel.
 *
 * Consolidates all blotter controls in the sidebar:
 * - Feed type selection (7 toggle buttons)
 * - Date picker with bounds
 * - Historical timestamp display
 * - Load More button (pagination)
 * - Expand All button (master/detail rows)
 *
 * Benefits:
 * - Cleaner main content area
 * - All filtering/controls in one place
 * - Consistent with AG Grid UX patterns
 * - Collapsible for more screen real estate
 */
const BlotterControlsPanel = (props: BlotterControlsPanelProps) => {
  const theme = useTheme();
  const {
    feedType,
    onFeedTypeChange,
    startDate,
    onStartDateChange,
    boundDates,
    historicalTimestamp,
    hasMore,
    onLoadMore,
    onExpandAll,
    allExpanded,
    showExpandAll,
    hasAccount,
  } = props;

  const handleFeedTypeChange = (_event: React.MouseEvent<HTMLElement>, newFeedType: BlotterFeedType | null) => {
    if (newFeedType !== null) {
      onFeedTypeChange(newFeedType);
    }
  };

  return (
    <div className="blotter-controls-panel">
      <div className="blotter-controls-section">
        <h3 className="blotter-controls-title">Feed Type</h3>
        <ToggleButtonGroup
          value={feedType}
          exclusive
          onChange={handleFeedTypeChange}
          disabled={!hasAccount}
          orientation="vertical"
          fullWidth
          size="small"
          className="blotter-feed-toggle-vertical"
        >
          <ToggleButton value={ORDER_EVENT} className="blotter-toggle-button">
            Orders
          </ToggleButton>
          <ToggleButton value={POSITION_UPDATE} className="blotter-toggle-button">
            Positions
          </ToggleButton>
          <ToggleButton value={BALANCE_UPDATE} className="blotter-toggle-button">
            Balances
          </ToggleButton>
          <ToggleButton value={ASSET_MARGIN_REPORT} className="blotter-toggle-button">
            Asset Margin
          </ToggleButton>
          <ToggleButton value={POSITION_MARGIN_REPORT} className="blotter-toggle-button">
            Position Margin
          </ToggleButton>
          <ToggleButton value={TRADE_EVENT} className="blotter-toggle-button">
            Trades
          </ToggleButton>
          <ToggleButton value={TRANSFER_EVENT} className="blotter-toggle-button">
            Transfers
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <Divider sx={{ my: 2 }} />

      <div className="blotter-controls-section">
        <h3 className="blotter-controls-title pulse-mb-1">Date Filter</h3>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate ?? undefined}
            onChange={onStartDateChange}
            minDate={boundDates.min ?? undefined}
            maxDate={boundDates.max ?? undefined}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                InputProps: {
                  sx: {
                    backgroundColor: theme.palette.bg["700"],
                    color: theme.palette.bg["50"],
                    borderRadius: "4px",
                    "& input": {
                      color: theme.palette.bg["50"],
                      padding: "8px 12px",
                    },
                    "& .MuiSvgIcon-root": {
                      color: theme.palette.bg["50"],
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.bg["600"],
                      "& .MuiSvgIcon-root": {
                        color: theme.palette.bg["50"],
                      },
                    },
                    "&.Mui-focused": {
                      backgroundColor: theme.palette.bg["700"],
                    },
                  },
                },
                InputLabelProps: {
                  sx: {
                    color: theme.palette.bg["50"],
                    "&.Mui-focused": {
                      color: theme.palette.bg["50"],
                    },
                  },
                },
                sx: {
                  "& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline": {
                    border: `1px solid ${theme.palette.bg["600"]}`,
                  },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline": {
                    border: `1px solid ${theme.palette.bg["50"]}`,
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
                    border: `1px solid ${theme.palette.bg["50"]}`,
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
        {historicalTimestamp && (
          <div className="blotter-historical-timestamp">
            <small>Historical @ {historicalTimestamp}</small>
          </div>
        )}
      </div>

      <Divider sx={{ my: 2 }} />

      <div className="blotter-controls-section">
        <h3 className="blotter-controls-title">Actions</h3>
        <div className="blotter-action-buttons-vertical">
          <Button
            variant="contained"
            size="small"
            color="info"
            fullWidth
            onClick={onLoadMore}
            disabled={!hasMore}
          >
            Load More
          </Button>
          {showExpandAll && (
            <Button
              variant="contained"
              size="small"
              color="info"
              fullWidth
              onClick={onExpandAll}
            >
              {allExpanded ? "Close All" : "Expand All"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlotterControlsPanel;
